/**
 * Keeps the Google Maps language in step with the site locale. [TENANT-5785]
 *
 * The Maps JavaScript API reads its `language` once, from the script URL, and cannot be reloaded
 * in place. A locale switch is an in-app navigation, so without a full page load a map keeps the
 * language it first loaded with. These helpers tell the two places that can see that happen — the
 * locale switch and a map mounting — when only a reload will do.
 */
import { googleMapsLanguage } from '../data/static/locale-config.js'
import { getLocaleFromPathname } from './locale-path.js'

export const GOOGLE_MAPS_SCRIPT_SELECTOR = 'script[src*="maps.googleapis.com/maps/api/js"]'

let mountedMapCount = 0

/**
 * Records a mounted map, so a locale switch knows the current page is showing one.
 *
 * @returns {() => void} releases the registration; safe to call more than once
 */
export function registerMountedMap () {
    mountedMapCount += 1
    let isReleased = false

    return () => {
        if (isReleased) return
        isReleased = true
        mountedMapCount -= 1
    }
}

/**
 * Maps `language` for a URL path, from its locale prefix.
 *
 * @param {string} [pathname]
 * @returns {string}
 */
export function googleMapsLanguageForPath (pathname) {
    return googleMapsLanguage(getLocaleFromPathname(typeof pathname === 'string' ? pathname : '/'))
}

/**
 * `language` the page's Maps script was requested with, or `undefined` when no script is on the
 * page (or it carries no language).
 *
 * @param {{ querySelector?: Function }} [root]
 * @returns {string | undefined}
 */
export function loadedGoogleMapsLanguage (root = globalThis.document) {
    const scriptSource = root?.querySelector?.(GOOGLE_MAPS_SCRIPT_SELECTOR)?.src
    if (typeof scriptSource !== 'string') return

    try {
        return new URL(scriptSource).searchParams.get('language') ?? undefined
    } catch {
        return
    }
}

/**
 * Whether the page's Maps script was loaded in a different language than `pathname` needs.
 *
 * @param {string} [pathname]
 * @param {{ querySelector?: Function }} [root]
 * @returns {boolean}
 */
export function isGoogleMapsLanguageStale (pathname, root = globalThis.document) {
    const loadedLanguage = loadedGoogleMapsLanguage(root)

    return loadedLanguage !== undefined && loadedLanguage !== googleMapsLanguageForPath(pathname)
}

/**
 * Whether switching to `targetLocale` needs a full page load: a map is on screen and its script
 * was loaded in another language. Pages without a map keep the in-app navigation.
 *
 * @param {string} targetLocale
 * @param {{ querySelector?: Function }} [root]
 * @returns {boolean}
 */
export function localeSwitchNeedsReload (targetLocale, root = globalThis.document) {
    if (mountedMapCount === 0) return false

    const loadedLanguage = loadedGoogleMapsLanguage(root)

    return loadedLanguage !== undefined && loadedLanguage !== googleMapsLanguage(targetLocale)
}
